import type { ReactNode } from "react";
import { grantStatus } from "../lib/connectorStatus";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { ListTable } from "./ListTable";
import {
  DetailLabel,
  DetailList,
  DetailRow,
  HoverActions,
  ListBox,
  NeutralIconBadge,
  PillBadge,
  RowNameContent,
  RowNameLayout,
} from "./Settings/shared";
import { Skeleton } from "./Skeleton";
import { Text } from "./Text";

type Account = {
  id: string;
  name: string;
  label?: string;
  description: string;
  icon: ReactNode;
  status: string;
  link?: string;
  onDisconnect: () => void;
};

export function ConnectedAccountsTable({
  accounts,
  label,
  loading,
  emptyText,
  emptyDescription,
}: {
  accounts: Account[];
  label: string;
  loading: boolean;
  emptyText: string;
  emptyDescription: string;
}) {
  const showLoading = loading && accounts.length === 0;
  if (accounts.length === 0 && !loading) {
    return (
      <DetailList>
        <DetailRow>
          <NeutralIconBadge iconName="circle-slash" />
          <DetailLabel>
            <Text variant="text-regular-md">{emptyText}</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              {emptyDescription}
            </Text>
          </DetailLabel>
        </DetailRow>
      </DetailList>
    );
  }

  return (
    <ListBox
      data-compact="true"
      role={showLoading ? "status" : "region"}
      aria-label={showLoading ? "Loading accounts…" : label}
      aria-busy={showLoading}
      style={{
        flex: "none",
        overflow: "hidden",
        pointerEvents: showLoading ? "none" : undefined,
      }}
    >
      <ListTable
        headers={{ name: "Name", status: "Status", actions: "" }}
        hideHeaders={showLoading}
        columns={{ status: "md" }}
        items={
          showLoading
            ? [
                {
                  key: "loading",
                  link: undefined,
                  name: (
                    <RowNameLayout aria-hidden="true">
                      <Skeleton
                        style={{ width: 32, height: 32, flexShrink: 0 }}
                      />
                      <RowNameContent style={{ gap: 6 }}>
                        <Skeleton style={{ width: 120 }} />
                        <Skeleton style={{ width: 80, height: 10 }} />
                      </RowNameContent>
                    </RowNameLayout>
                  ),
                  status: <Skeleton aria-hidden="true" style={{ width: 72 }} />,
                  actions: (_hasMouse: boolean) => (
                    <div aria-hidden="true" style={{ width: 24 }} />
                  ),
                },
              ]
            : accounts.map((account) => {
                const status = grantStatus(account.status);
                const label = account.label ?? account.name;
                return {
                  key: account.id,
                  link: account.link,
                  name: (
                    <RowNameLayout>
                      <div style={{ flexShrink: 0 }}>{account.icon}</div>
                      <RowNameContent title={label}>
                        <Text variant="text-regular-md" ellipsis>
                          {account.name}
                        </Text>
                        {account.description && (
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                            ellipsis
                          >
                            {account.description}
                          </Text>
                        )}
                      </RowNameContent>
                    </RowNameLayout>
                  ),
                  status: (
                    <PillBadge
                      style={{
                        background: `var(--${status.color}-200)`,
                        color: `var(--${status.color}-800)`,
                        flexShrink: 0,
                      }}
                    >
                      {status.label}
                    </PillBadge>
                  ),
                  actions: (hasMouse: boolean) => (
                    <HoverActions visible={hasMouse}>
                      <Dropdown
                        opener={
                          <Button
                            variant="secondary"
                            icon="more"
                            aria-label={`Account actions for ${label}`}
                          />
                        }
                        align="bottom"
                        minWidth={160}
                        sections={[
                          {
                            items: [
                              {
                                type: "button",
                                left: <Icon name="circle-minus" size={16} />,
                                label: "Disconnect account",
                                onClick: account.onDisconnect,
                              },
                            ],
                          },
                        ]}
                      />
                    </HoverActions>
                  ),
                };
              })
        }
      />
    </ListBox>
  );
}
